import { useEffect, useRef, useState } from "react"
import { Wallet, FileText, ChevronDown, ExternalLink } from "lucide-react"
import { formatCurrencyWhole, fullCurrency } from "../../../utils/formatCurrency.js"
import { shouldShowDashboardTds } from "../../../utils/userTds.js"

/**
 * Compact header chip for agent To-Pay due to company.
 * Sits in the PageHeader row next to Scan QR (mobile / iPad / desktop).
 */
const AgentMlDuesBanner = ({ dues, user, onViewMlSheets, expenseCredit = 0 }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  const outstanding = Number(dues?.total_amount_outstanding || 0)
  const sheets = Array.isArray(dues?.sheets) ? dues.sheets : []
  const sheetCount = dues?.pending_ml_sheets || sheets.length || 0
  const visible = Boolean(dues) && outstanding > 0

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDoc)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  if (!visible) return null

  return (
    <div ref={rootRef} className="relative shrink-0">
      <style>{`
        @keyframes mlDuesDotPing {
          0% { transform: scale(1); opacity: 0.9; }
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        .ml-dues-dot-ping {
          animation: mlDuesDotPing 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>

      <div className="inline-flex items-stretch rounded-lg border border-amber-400/90 bg-amber-50 shadow-sm overflow-hidden max-w-[9.5rem] xs:max-w-[11rem] sm:max-w-none">
        <button
          type="button"
          onClick={() => onViewMlSheets?.()}
          className="inline-flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-1 sm:py-1.5 min-w-0 hover:bg-amber-100/80 transition-colors"
          title={`Return to company — ${fullCurrency(outstanding)} outstanding`}
        >
          <span className="relative flex-shrink-0">
            <span className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-md bg-amber-500">
              <Wallet className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
            </span>
            <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
              <span className="ml-dues-dot-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
            </span>
          </span>

          <span className="min-w-0 text-left leading-tight">
            <span className="block text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-amber-800/80 truncate">
              Return
              {sheetCount > 0 ? (
                <span className="font-normal normal-case tracking-normal"> · {sheetCount}</span>
              ) : null}
            </span>
            <span className="block text-[11px] sm:text-xs font-bold text-amber-950 tabular-nums truncate">
              {formatCurrencyWhole(outstanding)}
            </span>
          </span>

          <ExternalLink className="hidden md:block w-3 h-3 text-amber-700/70 flex-shrink-0" />
        </button>

        {sheets.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center px-1 sm:px-1.5 border-l border-amber-300/80 text-amber-800 hover:bg-amber-100/80 transition-colors"
            aria-expanded={open}
            aria-label={open ? "Hide sheet details" : "Show sheet details"}
          >
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {open && sheets.length > 0 && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-[min(calc(100vw-2rem),20rem)] rounded-xl border border-amber-200 bg-white shadow-lg overflow-hidden">
          <div className="px-2.5 py-2 border-b border-amber-100 bg-amber-50/80">
            <p className="text-[11px] font-semibold text-amber-900">Send to company</p>
            <p className="text-[10px] text-amber-800/80">
              {sheetCount} sheet{sheetCount === 1 ? "" : "s"} · {fullCurrency(outstanding)}
            </p>
            <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-amber-900">
              <span>{dues?.remittance_kind === 'paid' ? 'Paid collected' : 'To-Pay collected'}</span>
              <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.total_gross_to_pay)}</span>
              <span>Commission</span>
              <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.total_commission_amount)}</span>
              {shouldShowDashboardTds(user, dues?.total_tds_amount) && (
                <>
                  <span>TDS</span>
                  <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.total_tds_amount)}</span>
                </>
              )}
              <span>You keep (net)</span>
              <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.total_net_commission)}</span>
              {Number(expenseCredit) > 0 && (
                <>
                  <span>Approved expenses</span>
                  <span className="text-right tabular-nums">−{formatCurrencyWhole(expenseCredit)}</span>
                </>
              )}
              {(dues?.remittance_kind === 'paid' || Number(dues?.company_paid_amount) > 0) && (
                <>
                  <span>Company paid</span>
                  <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.company_paid_amount)}</span>
                </>
              )}
              {(dues?.remittance_kind !== 'paid' || Number(dues?.paid_to_company_amount) > 0) && (
                <>
                  <span>Paid to company</span>
                  <span className="text-right tabular-nums">{formatCurrencyWhole(dues?.paid_to_company_amount)}</span>
                </>
              )}
              <span className="font-semibold">Send to company</span>
              <span className="text-right font-semibold tabular-nums">{formatCurrencyWhole(outstanding)}</span>
            </div>
          </div>
          <ul className="max-h-44 overflow-y-auto divide-y divide-amber-50">
            {sheets.slice(0, 10).map((sheet) => (
              <li
                key={sheet.ml_sheet_id}
                className="flex items-center justify-between gap-2 px-2.5 py-2"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <FileText className="w-3 h-3 text-amber-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-gray-800 truncate">
                      {sheet.ml_sheet_id}
                    </p>
                    <p className="text-[10px] text-gray-500">{sheet.status || "OPEN"}</p>
                  </div>
                </div>
                <span
                  className="text-[11px] font-semibold text-red-700 tabular-nums flex-shrink-0"
                  title={fullCurrency(sheet.amount_outstanding)}
                >
                  {formatCurrencyWhole(sheet.amount_outstanding)}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onViewMlSheets?.()
            }}
            className="w-full text-center text-[11px] font-semibold text-blue-700 hover:bg-blue-50 px-2.5 py-2 border-t border-amber-100"
          >
            View Settlements
          </button>
        </div>
      )}
    </div>
  )
}

export default AgentMlDuesBanner
